
import Vue, { PropType } from 'vue';
import { mapState } from 'vuex';
import { FeedbackReport, FeedbackReportOriginKind, FeedbackReportPriority } from '@run-diffusion/shared';
import FeedbackChip from './FeedbackChip.vue';
import { feedbackFileUrl } from './feedbackFiles';
import { FeedbackReportStatus } from '@run-diffusion/shared';
import { currentHolder, hasBoard, isAdminReport, KIND_LABELS, listRowOf, ORIGIN_LABELS, originKind, ReportHolder, STATUS_LABELS, statusOf, tagsOf } from './feedbackReports';

const KIND_ICONS: Record<string, string> = {
	bug: 'mdi-bug-outline',
	confusing: 'mdi-help-circle-outline',
	idea: 'mdi-lightbulb-on-outline',
};

// One report in the Feedback admin page's list: its first screenshot, loaded once the row scrolls
// into view, and what it is, where it stands (its pull request merged, its tags), and who sent it:
// a tester on a board, a developer's agent that filed it or imported it from a list (with the
// row's id in that list), or a person here.
export default Vue.extend({
	name: 'FeedbackReportRow',
	props: {
		report: { type: Object as PropType<FeedbackReport>, required: true },
		selected: { type: Boolean, default: false },
	},
	data () {
		return {
			previewUrl: '',
			previewLoaded: false,
			observer: null as IntersectionObserver | null,
		};
	},
	computed: {
		...mapState([
			'user',
		]),
		// Who is working on it, if anyone: shown so two people don't pick up the same report
		holder (): ReportHolder | null {
			return currentHolder(this.report);
		},
		holdsIt (): boolean {
			return !!this.holder && !!this.user && this.holder.uid === this.user.id;
		},
		holderLabel (): string {
			if (!this.holder) return '';
			if (this.holder.kind === 'agent') return this.holdsIt ? `Your ${this.holder.agent || 'agent'}` : (this.holder.agent || 'Agent');
			return this.holdsIt ? 'You' : 'Claimed';
		},
		holderTitle (): string {
			if (!this.holder) return '';
			return `Being worked on by ${this.holder.name || (this.holdsIt ? 'you' : 'someone')}`;
		},
		kindLabel (): string {
			return KIND_LABELS[this.report.kind];
		},
		kindIcon (): string {
			return KIND_ICONS[this.report.kind] || 'mdi-message-outline';
		},
		status (): FeedbackReportStatus {
			return statusOf(this.report);
		},
		statusLabel (): string {
			return STATUS_LABELS[this.status];
		},
		merged (): boolean {
			return !!this.report.triage?.mergedAt && this.status === 'in_pr';
		},
		tags (): string[] {
			return tagsOf(this.report);
		},
		priority (): FeedbackReportPriority | null {
			return this.report.triage?.priority || null;
		},
		isAdmin (): boolean {
			return isAdminReport(this.report);
		},
		// the board it was sent from: one an agent filed or imported has none (an empty placeholder, or null)
		boardKnown (): boolean {
			return hasBoard(this.report.board);
		},
		// filed by a developer's agent, or imported from a list; null for a tester's report from a board
		origin (): FeedbackReportOriginKind | null {
			return originKind(this.report);
		},
		originLabel (): string {
			return this.origin ? ORIGIN_LABELS[this.origin] : '';
		},
		// the row of a list it came from, or was linked to
		externalId (): string {
			return listRowOf(this.report)?.externalId || '';
		},
		originList (): string {
			return listRowOf(this.report)?.list || '';
		},
		originName (): string {
			return this.report.origin?.name || (this.origin === 'page' ? 'Filed here' : 'Filed by an agent');
		},
		importTitle (): string {
			const list: string = this.originList || 'a list';
			return this.externalId ? `Row ${this.externalId} in ${list}` : `Imported from ${list}`;
		},
		sentAt (): Date | null {
			return this.report.createdAt ? this.report.createdAt.toDate() : null;
		},
	},
	mounted () {
		if (!this.report.shots.length) return;
		if (typeof IntersectionObserver === 'undefined') {
			this.loadPreview();
			return;
		}
		this.observer = new IntersectionObserver((entries) => {
			if (!entries.some((entry) => entry.isIntersecting)) return;
			this.stopObserving();
			this.loadPreview();
		}, { rootMargin: '200px' });
		this.observer.observe(this.$el);
	},
	beforeDestroy () {
		this.stopObserving();
	},
	methods: {
		stopObserving () {
			if (this.observer) this.observer.disconnect();
			this.observer = null;
		},
		async loadPreview () {
			try {
				this.previewUrl = await feedbackFileUrl(this.report.shots[0].path);
			} catch (e) {
				// the kind's icon stands in
			}
		},
	},
	components: {
		FeedbackChip,
	},
});
